import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import {
	ServerDataResponse,
	WorkflowLangMetadata,
	WorkflowTemplateList,
	WorkflowTemplateListItem
} from '@models/models';
import { MessageService } from '@services/helpers/message-service/message.service';
import { catchError, map } from 'rxjs/operators';
import { LanguageItem } from '@models/brands.model';

interface WorkflowsNames {
	errorMessage: any;
	message: any;
	id: number;
	name: string;
}

@Injectable()
export class WorkflowDataProvider {
	private ALL_WORKFLOWS_URL = '/protected/rest/workflows';
	private JOB_WORKFLOW_URL = '/protected/rest/workflow';

	private defaultWorkflow: any = {
		name: 'Default',
		id: null
	};

	workflowNames: Array<WorkflowTemplateListItem> = [];
	languages: {
		availableList: Array<LanguageItem>;
		selected: LanguageItem;
	};

	constructor(
		private http: HttpClient,
		private messageService: MessageService
	) {}

	getWorkflowNames(): Observable<Array<WorkflowTemplateListItem>> {
		return this.getWorkflowNamesFromServer().pipe(
			map((data: ServerDataResponse<WorkflowTemplateList>) => {
				if (data !== undefined && data !== null && data.data.workflows) {
					this.setWorkflowNamesInStorage(data.data.workflows);
					return JSON.parse(JSON.stringify(this.workflowNames));
				}
				return data as ServerDataResponse<WorkflowTemplateList>;
			}),
			catchError((err): Observable<Error> => {
				this.messageService.handleError(err);
				return Observable.throw(err);
			})
		);
	}

	getWorkflowAndLanguageNames() {
		return this.getWorkflowNamesFromServer().pipe(
			map((data: ServerDataResponse<WorkflowLangMetadata>) => {
				if (data && data.data.workflows) {
					this.setWorkflowNamesInStorage(data.data.workflows);
					return {
						workflows: JSON.parse(JSON.stringify(this.workflowNames)),
						languages: JSON.parse(JSON.stringify(data.data.languages || null))
					} as WorkflowLangMetadata;
				}
				return data as ServerDataResponse<WorkflowLangMetadata>;
			}),
			catchError((err): Observable<Error> => {
				this.messageService.handleError(err);
				return Observable.throw(err);
			})
		);
	}

	saveTask(task) {
		const url = `${this.JOB_WORKFLOW_URL}/task/`;
		return this.http.post(url, task).map((data: any) => {
			data.taskToAnimate = task.number;
			return data;
		});
	}

	deleteTask(id) {
		const url = `${this.JOB_WORKFLOW_URL}/task/${id}`;
		return this.http.delete(url);
	}

	getTaskTemplates(id) {
		const url = `${this.ALL_WORKFLOWS_URL}/get`;
		const params = new HttpParams().set('id', id);
		return this.http.get(url, { params });
	}

	saveWorkflow(workflow) {
		const url = `${this.ALL_WORKFLOWS_URL}/save`;
		return this.http.post(url, workflow);
	}

	getWorkflow(id) {
		const url = `/protected/rest/jobs/workflow/?jobId=${id}`;
		return this.http.get(url);
	}

	validateEmailLinks(action, emailTemplateId) {
		const url = `${this.ALL_WORKFLOWS_URL}/validate`;
		const body = { action, emailTemplateId };
		return this.http.post(url, body);
	}

	validateEmailName(email) {
		const url = `${this.ALL_WORKFLOWS_URL}/email_template/name`;
		return this.http.post(url, email);
	}

	saveNewTasksOrder(taskId, newNumber, type, overdueDays) {
		const url = `${this.JOB_WORKFLOW_URL}/dragAndDrop`;
		const body = {
			taskId,
			newNumber,
			type,
			overdueDays
		};

		return this.http.post(url, body);
	}

	getWorkflowsOnPage({ perPage, page, pageOrder: { predicate, reverse } }) {
		const url = this.ALL_WORKFLOWS_URL;
		let params = new HttpParams();
		params = params.append('perPage', perPage);
		params = params.append('page', page);
		params = params.append('predicate', predicate);
		params = params.append('reverse', reverse);

		return this.http.get(url, { params });
	}

	deleteWorkflow(id) {
		const url = `${this.ALL_WORKFLOWS_URL}/delete?id=${id}`;
		return this.http.post(url, null);
	}

	getWorkflowNamesFromServer(): Observable<
		ServerDataResponse<WorkflowLangMetadata>
	> {
		const url = '/api/user/v1/job/create/metadata';
		return this.http.get(url) as Observable<
			ServerDataResponse<WorkflowLangMetadata>
		>;
	}

	private isDefaultWorkflowInList(workflows) {
		return workflows.some(
			workflow => workflow.name === this.defaultWorkflow.name
		);
	}

	private setWorkflowNamesInStorage(data): void {
		this.workflowNames = JSON.parse(JSON.stringify(data));
		if (!this.isDefaultWorkflowInList(this.workflowNames)) {
			this.workflowNames.push(this.defaultWorkflow);
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./shared/services/workflow/workflow.data.service.ts